import { paramBlank, paramDsName, paramNil, paramValue, paramXPath } from "../constants/parameterNames";
import { typeBoolean, typeDataSource, typeXPath } from "../constants/parameterTypes";
import { getBoolParamValue, getParamValue, hasParam } from "../helpers/parameter.accessors";
import { getDataSource } from "../helpers/qRules.dataSource.helpers";
import { getValidNodeSet, setNil } from "../helpers/qRules.node.helpers";

const description = "Sets the value of one or more fields. Can be used to specify a particular field (like in a repeating group) using XPath filters.";

const optionalParameters = {
    [paramDsName]: {
        description: 'Data source name of the field(s) to set.',
        type: typeDataSource,
    },
    [paramValue]: {
        description: 'New value for the field.',
    },
    [paramBlank]: {
        description: '(boolean) Sets the field to blank. Overrides /value if specified',
        type: typeBoolean,
    },
    [paramNil]: {
        description: '(boolean) Adds an xsi:nil="true" attribute to the field. For use with non-string data types. Overrides /value if specified.',
        type: typeBoolean,
    },
};

const requiredParameters = {
    [paramXPath]: {
        description: 'XPath to field(s) to set.',
        type: typeXPath,
        dsParentName: paramDsName,
    },
};

const setValueErrorNoValueSpecified = "A value should be specified when not setting blank or nil.";
const setValueErrorFailedToSetValue = "Failed to set value of target field: ";

const getValueToUse = (specifiedValue, blank) =>
    // /blank=true overrides any /value value.
    blank
        ? ''
        : specifiedValue;

const setSingleField = async (target, nil, value) => {
    if (nil) {
        setNil(target);
    } else {
        await target.setValueAsync(value);
    }
};

const executeAsync = async ({ dataSources, parameters }) => {
    const dsname = getParamValue(parameters, paramDsName);
    const xpath = getParamValue(parameters, paramXPath);
    const value = getParamValue(parameters, paramValue);
    const blank = getBoolParamValue(parameters, paramBlank);
    const nil = getBoolParamValue(parameters, paramNil);

    const validValueSpecification = blank || nil || hasParam(parameters, paramValue);

    // 47686 - Allow blank /value if it is specified
    if (!validValueSpecification) {
        throw new Error(setValueErrorNoValueSpecified);
    }

    const valueToUse = getValueToUse(value, blank);

    const targets = getValidNodeSet(
        getDataSource(dataSources, dsname),
        xpath,
    );

    try {
        for (const target of targets) {
            await setSingleField(target, nil, valueToUse);
        }

        return '';
    } catch (e) {
        console.error(e);

        throw new Error(setValueErrorFailedToSetValue + e.message);
    }
};

const SetValue = {
    description,
    executeAsync,
    optionalParameters,
    requiredParameters,
};

export default SetValue;